/**
 * Astitva Living — Quick View Controller
 * Fetches product data via AJAX (/products/{handle}.js) and renders dynamically in a modal
 */

(function () {
  'use strict';

  class QuickView {
    constructor() {
      this.modal = document.getElementById('quick-view-modal');
      if (!this.modal) return;

      this.closeBtn = this.modal.querySelector('[data-quick-view-close]');
      this.content = this.modal.querySelector('[data-quick-view-content]');

      this.init();
    }

    init() {
      // Delegate click event for quick view triggers
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-view-trigger]');
        if (trigger) {
          e.preventDefault();
          const handle = trigger.dataset.productHandle;
          this.open(handle);
        }
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.open) this.close();
      });
    }

    async open(handle) {
      if (!handle) return;
      this.modal.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
      this.renderLoading();

      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) throw new Error('Product not found');
        const product = await response.json();
        this.renderProduct(product);
      } catch (error) {
        this.renderError();
      }
    }

    close() {
      this.modal.close();
      document.documentElement.removeAttribute('scroll-lock');
    }

    renderLoading() {
      this.content.innerHTML = `
        <div class="quick-view-modal__loading">
          <div class="skeleton ratio-portrait" style="height: 400px; width: 100%;"></div>
          <div class="quick-view-modal__loading-details">
            <div class="skeleton" style="height: 24px; width: 80%; margin-bottom: var(--space-4);"></div>
            <div class="skeleton" style="height: 18px; width: 40%; margin-bottom: var(--space-6);"></div>
            <div class="skeleton" style="height: 48px; width: 100%; margin-bottom: var(--space-4);"></div>
            <div class="skeleton" style="height: 48px; width: 100%;"></div>
          </div>
        </div>
      `;
    }

    renderError() {
      this.content.innerHTML = `
        <p class="quick-view-modal__error text-center text-muted">
          Sorry, this product could not be loaded. Please try again.
        </p>
      `;
    }

    renderProduct(product) {
      const featuredImage = product.featured_image || '';
      const moneySymbol = '₹';
      
      const formatMoney = (cents) => {
        return (cents / 100).toLocaleString('en-IN', {
          style: 'currency',
          currency: 'INR'
        });
      };

      const optionsHTML = product.options.map((opt, optIndex) => {
        const valuesHTML = opt.values.map((val, valIndex) => {
          const checked = valIndex === 0 ? 'checked' : '';
          return `
            <div class="quick-view-option__value">
              <input 
                type="radio" 
                id="qv-opt-${opt.name}-${valIndex}" 
                name="options[${opt.name}]" 
                value="${val}" 
                class="quick-view-option__input" 
                data-option-index="${optIndex}"
                ${checked}
              >
              <label for="qv-opt-${opt.name}-${valIndex}" class="quick-view-option__label">
                ${val}
              </label>
            </div>
          `;
        }).join('');

        return `
          <fieldset class="quick-view-option">
            <legend class="quick-view-option__title eyebrow">${opt.name}</legend>
            <div class="quick-view-option__list">
              ${valuesHTML}
            </div>
          </fieldset>
        `;
      }).join('');

      this.content.innerHTML = `
        <div class="quick-view-grid">
          <div class="quick-view-media ratio-container ratio-portrait">
            <img src="${featuredImage}" alt="${product.title}" class="quick-view-img" loading="eager">
          </div>
          
          <div class="quick-view-info">
            <p class="quick-view-vendor eyebrow">${product.vendor}</p>
            <h2 class="quick-view-title">${product.title}</h2>
            
            <div class="quick-view-price-wrapper">
              <span class="quick-view-price" data-qv-price>${formatMoney(product.price)}</span>
              ${product.compare_at_price > product.price 
                ? `<span class="quick-view-price-compare" data-qv-compare-price>${formatMoney(product.compare_at_price)}</span>` 
                : ''
              }
            </div>

            <form method="post" action="/cart/add" id="quick-view-form" class="quick-view-form">
              <input type="hidden" name="id" value="${product.variants[0].id}">
              
              <div class="quick-view-options">
                ${optionsHTML}
              </div>

              <div class="quick-view-atc-row">
                <div class="qty-input" data-qty-input>
                  <button type="button" class="qty-input__btn" data-qty-minus aria-label="Decrease quantity">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 12h14" stroke-linecap="round"/></svg>
                  </button>
                  <input type="number" name="quantity" value="1" min="1" class="qty-input__value" data-qty-value>
                  <button type="button" class="qty-input__btn" data-qty-plus aria-label="Increase quantity">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 5v14M5 12h14" stroke-linecap="round"/></svg>
                  </button>
                </div>

                <button type="submit" class="btn btn--primary flex-1" data-qv-submit>
                  <span>Add to Cart</span>
                </button>
              </div>
            </form>

            <a href="${product.url}" class="quick-view-details-link btn btn--secondary btn--full">
              View Full Details
            </a>
          </div>
        </div>
      `;

      this.initProductLogic(product);
    }

    initProductLogic(product) {
      const form = this.content.querySelector('#quick-view-form');
      if (!form) return;

      const variantIdInput = form.querySelector('input[name="id"]');
      const inputs = Array.from(form.querySelectorAll('.quick-view-option__input'));
      const priceEl = this.content.querySelector('[data-qv-price]');
      const comparePriceEl = this.content.querySelector('[data-qv-compare-price]');
      const submitBtn = form.querySelector('[data-qv-submit]');

      const updateVariant = () => {
        const selectedOptions = product.options.map((opt, index) => {
          const checkedRadio = inputs.find(input => input.dataset.optionIndex === index.toString() && input.checked);
          return checkedRadio ? checkedRadio.value : '';
        });

        const currentVariant = product.variants.find(variant => {
          return !variant.options.map((option, index) => {
            return selectedOptions[index] === option;
          }).includes(false);
        });

        if (currentVariant) {
          variantIdInput.value = currentVariant.id;
          
          // Update Price
          priceEl.textContent = (currentVariant.price / 100).toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR'
          });

          if (comparePriceEl) {
            if (currentVariant.compare_at_price > currentVariant.price) {
              comparePriceEl.textContent = (currentVariant.compare_at_price / 100).toLocaleString('en-IN', {
                style: 'currency',
                currency: 'INR'
              });
              comparePriceEl.style.display = 'inline';
            } else {
              comparePriceEl.style.display = 'none';
            }
          }

          // Update availability
          if (currentVariant.available) {
            submitBtn.removeAttribute('disabled');
            submitBtn.querySelector('span').textContent = 'Add to Cart';
          } else {
            submitBtn.setAttribute('disabled', 'disabled');
            submitBtn.querySelector('span').textContent = 'Sold Out';
          }

          // Update featured image if variant has one
          if (currentVariant.featured_image) {
            const img = this.content.querySelector('.quick-view-img');
            if (img) img.src = currentVariant.featured_image.src;
          }

        } else {
          // Variant doesn't exist
          submitBtn.setAttribute('disabled', 'disabled');
          submitBtn.querySelector('span').textContent = 'Unavailable';
        }
      };

      inputs.forEach(input => input.addEventListener('change', updateVariant));

      // Qty stepper inside quick-view
      const qtyWrapper = form.querySelector('[data-qty-input]');
      if (qtyWrapper) {
        const minus = qtyWrapper.querySelector('[data-qty-minus]');
        const plus = qtyWrapper.querySelector('[data-qty-plus]');
        const input = qtyWrapper.querySelector('[data-qty-value]');

        minus.addEventListener('click', () => {
          const val = parseInt(input.value, 10);
          if (val > 1) input.value = val - 1;
        });

        plus.addEventListener('click', () => {
          const val = parseInt(input.value, 10);
          input.value = val + 1;
        });
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    window.AstitvaQuickView = new QuickView();
  });

})();
